import { HttpApiSchema } from "@effect/platform";
import { Predicate, Schema } from "effect";
import type { EmailProviderType } from "../conversation/channel-utils";

export const EMAIL_ALREADY_CONNECTED_TO_ANOTHER_TEAM_MESSAGE =
  "This email is already connected to a different team. To connect it to this team, remove it from the other team first.";

const EMAIL_PROVIDER_LABELS: Record<EmailProviderType, string> = {
  gmail: "a Gmail mailbox",
  outlook: "an Outlook mailbox",
  custom_email: "a custom email",
};

export const emailConnectedWithAnotherProviderMessage = (
  provider: EmailProviderType,
) =>
  `This email is already connected to this team as ${EMAIL_PROVIDER_LABELS[provider]}. To connect it another way, remove that connection first.`;

export class EmailAlreadyConnectedToAnotherTeamError extends Schema.TaggedError<EmailAlreadyConnectedToAnotherTeamError>()(
  "EmailAlreadyConnectedToAnotherTeamError",
  { email: Schema.String },
  HttpApiSchema.annotations({ status: 409 }),
) {}

export const isEmailAlreadyConnectedToAnotherTeamError = (
  error: unknown,
): error is EmailAlreadyConnectedToAnotherTeamError =>
  Predicate.hasProperty(error, "_tag") &&
  error._tag === "EmailAlreadyConnectedToAnotherTeamError";

export class EmailConnectedWithAnotherProviderError extends Schema.TaggedError<EmailConnectedWithAnotherProviderError>()(
  "EmailConnectedWithAnotherProviderError",
  {
    email: Schema.String,
    provider: Schema.Literal("gmail", "outlook", "custom_email"),
  },
  HttpApiSchema.annotations({ status: 409 }),
) {}

export const isEmailConnectedWithAnotherProviderError = (
  error: unknown,
): error is EmailConnectedWithAnotherProviderError =>
  Predicate.hasProperty(error, "_tag") &&
  error._tag === "EmailConnectedWithAnotherProviderError";

/** Why connecting an email address was refused. */
export type EmailConnectRefusal =
  | { readonly reason: "another_team" }
  | {
      readonly reason: "another_provider";
      readonly provider: EmailProviderType;
    };

export const emailConnectRefusalMessage = (refusal: EmailConnectRefusal) =>
  refusal.reason === "another_team"
    ? EMAIL_ALREADY_CONNECTED_TO_ANOTHER_TEAM_MESSAGE
    : emailConnectedWithAnotherProviderMessage(refusal.provider);
